import { Skeleton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Tabs } from '@/components/TSX components';
import { useGetSearchedColumns } from '../api/dataCatalog/getSearchedColumns';
import ToolTip from '@/components/Tooltip';
import { Button } from '@/components/TSX components';
import { useGetSearchedTables } from '../api/dataCatalog/getSearchedTables';

export const SearchResults = ({
  tableId,
  searchQuery,
  handleTableClick,
  handleColumnClick,
  tabIndex,
  setTabIndex,
  setSearchQuery,
  setImmediateSearchQuery,
  immediateSearchQuery,
}) => {
  const [tooltipShow, setTooltipShow] = useState(false);
  const [tableRefetch, setTableRefetch] = useState(false);
  const [colRefetch, setColRefetch] = useState(false);

  const wrapperRef = useRef(null);

  const [tablePageNumber, setTablePageNumber] = useState(1);
  const [columnPageNumber, setColumnPageNumber] = useState(1);
  const [tablePageCount, setTablePageCount] = useState(0);
  const [columnPageCount, setColumnPageCount] = useState(0);

  const tableWrapperRef = useRef(null);
  const columnWrapperRef = useRef(null);

  const tablesListData = useGetSearchedTables({
    // keyword: searchQuery,
    tableNameKeyword: searchQuery,
    // pageNumber: tablePageNumber,
    config: {
      staleTime: Infinity,
    },
  });

  const columnListData = useGetSearchedColumns({
    keyword: searchQuery,
    // pageNumber: columnPageNumber,
    sortTableOnTop: tableId.name,
    config: {
      staleTime: Infinity,
    },
  });

  useEffect(() => {
    setTablePageNumber(1);
    setColumnPageNumber(1);
    setTableRefetch(false);
    setColRefetch(false);
  }, [immediateSearchQuery]);

  useEffect(() => {
    if (
      tableWrapperRef.current &&
      Array.isArray(tablesListData.data?.pages) &&
      tablesListData.data?.pages.length === 1
    ) {
      const tabElems = Array.from(tableWrapperRef.current.children);
      const tabElem = tabElems?.[0];
      tabElem?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [tablesListData.data]);

  useEffect(() => {
    if (
      columnWrapperRef.current &&
      Array.isArray(columnListData.data?.pages) &&
      columnListData.data?.pages.length === 1
    ) {
      const colElems = Array.from(columnWrapperRef.current.children);
      const colElem = colElems?.[0];
      colElem?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [columnListData.data]);

  useEffect(() => {
    if (tableWrapperRef.current) {
      const tabElems = Array.from(tableWrapperRef.current.children);
      const tabElem = tabElems?.[0];
      tabElem?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    if (columnWrapperRef.current) {
      const colElems = Array.from(columnWrapperRef.current.children);
      const colElem = colElems?.[0];
      colElem?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, [tabIndex]);

  useEffect(() => {
    if (colRefetch && columnListData.hasNextPage) {
      columnListData.fetchNextPage();
      setColumnPageNumber((prev) => prev + 1);
    }
  }, [colRefetch]);

  useEffect(() => {
    if (tableRefetch && tablesListData.hasNextPage) {
      tablesListData.fetchNextPage();
      setTablePageNumber((prev) => prev + 1);
    }
  }, [tableRefetch]);

  useEffect(() => {
    if (
      !tablesListData.isFetching &&
      Array.isArray(tablesListData.data?.pages)
    ) {
      const len = tablesListData.data.pages.length;
      if (
        Array.isArray(tablesListData.data.pages?.[len - 1]?.data) &&
        tablesListData.data.pages[len - 1].data.length > 0
      ) {
        setTableRefetch(false);
      }
    }
  }, [tablesListData.isFetching]);

  useEffect(() => {
    if (
      !columnListData.isFetching &&
      Array.isArray(columnListData.data?.pages)
    ) {
      const len = columnListData.data.pages.length;
      if (
        Array.isArray(columnListData.data.pages?.[len - 1]?.data) &&
        columnListData.data.pages[len - 1].data.length > 0
      ) {
        setColRefetch(false);
      }
    }
  }, [columnListData.isFetching]);

  useEffect(() => {
    if (
      Array.isArray(tablesListData.data?.pages?.[0]?.data) &&
      tablesListData.data.pages[0].data.length > 0 &&
      tablesListData.data.pages[0]?.totalPages
    ) {
      setTablePageCount(tablesListData.data.pages[0].totalPages);
    }
  }, [tablesListData.data]);

  useEffect(() => {
    if (
      Array.isArray(columnListData.data?.pages?.[0]?.data) &&
      columnListData.data.pages[0].data.length > 0 &&
      columnListData.data.pages[0]?.totalPages
    ) {
      setColumnPageCount(columnListData.data.pages[0].totalPages);
    }
  }, [columnListData.data]);

  let tabList =
    tablesListData.data?.pages
      ?.map((page) => Array.isArray(page?.data) && [...page.data])
      .flat() || [];

  let colList =
    columnListData.data?.pages
      ?.map((page) => Array.isArray(page?.data) && [...page.data])
      .flat() || [];

  const HighlightSearchText = ({ text }) => {
    return (
      <span className="pointer-events-none">
        {text.toLowerCase().includes(searchQuery.toLowerCase()) ? (
          <span>
            <span>
              {text.slice(
                0,
                text.toLowerCase().indexOf(searchQuery.toLowerCase())
              )}
            </span>
            <span className="font-medium">
              {text.slice(
                text.toLowerCase().indexOf(searchQuery.toLowerCase()),
                text.toLowerCase().indexOf(searchQuery.toLowerCase()) +
                  searchQuery.length
              )}
            </span>
            <span>
              {text.slice(
                text.toLowerCase().indexOf(searchQuery.toLowerCase()) +
                  searchQuery.length
              )}
            </span>
          </span>
        ) : (
          text
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-col w-full p-2 gap-2" ref={wrapperRef}>
      <div className="h-fit border-b border-pv-neutral-grey-100">
        <Tabs
          size="small"
          extended
          className="w-fit h-full flex flex-col"
          navClassName="px-2"
          activeIndex={tabIndex}
          headerActionClassName="py-2 px-3"
          showDefaultBottomBorder={false}
          onTabChange={(e) => {
            setTabIndex(e.index);
          }}
          tabData={[
            {
              label: (
                <span className="flex items-center gap-2.5 text-pv-text-primary-text hover:text-pv-primary-primary-500">
                  <span className="text-xs">Tables</span>
                  <span
                    className={`text-xxs font-normal
                      ${
                        tabIndex === 0
                          ? 'bg-pv-primary-primary-500 text-white'
                          : 'bg-pv-neutral-grey-100 text-pv-neutral-grey-400'
                      }
                    py-0.5 p-1 rounded-md`}
                  >
                    {tablesListData.data?.pages?.[0]?.total || 0}
                  </span>
                </span>
              ),
            },
            {
              label: (
                <span className="flex items-center gap-2.5 text-pv-text-primary-text hover:text-pv-primary-primary-500">
                  <span className="text-xs">Columns</span>
                  <span
                    className={`text-xxs font-normal
                      ${
                        tabIndex === 1
                          ? 'bg-pv-primary-primary-500 text-white'
                          : 'bg-pv-neutral-grey-100 text-pv-neutral-grey-400'
                      }
                    py-0.5 px-1 rounded-md`}
                  >
                    {columnListData.data?.pages?.[0]?.total || 0}
                  </span>
                </span>
              ),
            },
          ]}
        />
      </div>
      <div>
        {tabIndex === 0 ? (
          <div className="flex flex-col gap-2">
            {tablesListData.isLoading && tablePageNumber === 1 ? (
              <div className="flex flex-col">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div className="flex justify-between py-1">
                    <Skeleton
                      variant="rounded"
                      width={index % 2 === 0 ? '40%' : '20%'}
                      height={20}
                      sx={{ bgcolor: '#EEF0F7' }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={index % 2 === 0 ? '20%' : '30%'}
                      height={20}
                      sx={{ bgcolor: '#EEF0F7' }}
                    />
                  </div>
                ))}
              </div>
            ) : tabList.length > 0 ? (
              <div
                className="flex flex-col overflow-y-auto"
                style={{
                  maxHeight: '224px',
                }}
                onScroll={(e) => {
                  setTooltipShow(false);
                  if (e.target.scrollHeight - e.target.scrollTop < 1000) {
                    setTableRefetch(true);
                  }
                }}
                ref={tableWrapperRef}
              >
                {tabList.map((tab) => {
                  return (
                    <div
                      className="flex items-center justify-between text-xs py-1 px-2 hover:bg-pv-primary-primary-50 cursor-pointer rounded"
                      onClick={() => handleTableClick(tab)}
                      style={{
                        height: '28px',
                      }}
                    >
                      <div className="flex items-center">
                        <ToolTip
                          placement="top"
                          title={<HighlightSearchText text={tab.name} />}
                          tooltipActive={tooltipShow}
                          displayTooltipOnOverflow
                        >
                          <span
                            onMouseEnter={() => {
                              setTooltipShow(true);
                            }}
                            className="col-txt overflow-hidden whitespace-nowrap overflow-ellipsis"
                            style={{
                              maxWidth: '200px',
                            }}
                          >
                            <HighlightSearchText text={tab.name} />
                          </span>
                        </ToolTip>
                      </div>
                      <div className="flex items-center gap-1">
                        <img
                          src={tab.integration_logo}
                          style={{
                            width: '16px',
                            aspectRatio: 1,
                          }}
                        />
                        <div className="flex items-center text-pv-text-secondary-text">
                          <ToolTip
                            placement="top"
                            title={tab.datasource_description}
                            tooltipActive={tooltipShow}
                            displayTooltipOnOverflow
                          >
                            <span
                              onMouseEnter={() => {
                                setTooltipShow(true);
                              }}
                              className="col-txt overflow-hidden whitespace-nowrap overflow-ellipsis"
                              style={{
                                maxWidth: '150px',
                              }}
                            >
                              {tab.datasource_description}
                            </span>
                          </ToolTip>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {tablePageNumber <= tablePageCount &&
                  tabList?.length < tablesListData.data?.pages?.[0]?.total && (
                    <div className="flex flex-col">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div className="flex justify-between py-1">
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '40%' : '20%'}
                            height={20}
                            sx={{ bgcolor: '#EEF0F7' }}
                          />
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '20%' : '30%'}
                            height={20}
                            sx={{ bgcolor: '#EEF0F7' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span
                  className="text-xs px-2 py-1 text-pv-text-secondary-text"
                  style={{
                    height: '28px',
                  }}
                >
                  No results found. Try refining your search.
                </span>
                <Button
                  btnColor="secondary"
                  btnSize="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setImmediateSearchQuery('');
                  }}
                >
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {columnListData.isLoading && columnPageNumber === 1 ? (
              <div className="flex flex-col">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div className="flex justify-between py-1">
                    <Skeleton
                      variant="rounded"
                      width={index % 2 === 0 ? '40%' : '20%'}
                      height={20}
                      sx={{ bgcolor: '#EEF0F7' }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={index % 2 === 0 ? '20%' : '30%'}
                      height={20}
                      sx={{ bgcolor: '#EEF0F7' }}
                    />
                  </div>
                ))}
              </div>
            ) : colList.length > 0 ? (
              <div
                className="flex flex-col overflow-y-auto"
                style={{
                  maxHeight: '224px',
                }}
                onScroll={(e) => {
                  setTooltipShow(false);
                  if (e.target.scrollHeight - e.target.scrollTop < 1000) {
                    setColRefetch(true);
                  }
                }}
                ref={columnWrapperRef}
              >
                {colList.map((col) => {
                  return (
                    <div
                      className="flex items-center justify-between text-xs py-1 px-2 hover:bg-pv-primary-primary-50 cursor-pointer rounded"
                      onClick={() => {
                        handleColumnClick(col);
                      }}
                      style={{
                        height: '28px',
                      }}
                    >
                      <div className="flex gap-2 items-center">
                        <ToolTip
                          placement="top"
                          title={<HighlightSearchText text={col.label} />}
                          tooltipActive={tooltipShow}
                          displayTooltipOnOverflow
                        >
                          <span
                            onMouseEnter={() => {
                              setTooltipShow(true);
                            }}
                            className="col-txt whitespace-nowrap overflow-ellipsis overflow-hidden"
                            style={{
                              maxWidth: '140px',
                            }}
                          >
                            <HighlightSearchText text={col.label} />
                          </span>
                        </ToolTip>
                        <div className="flex items-center bg-pv-primary-primary-100 px-1.5 py-0.5 text-xs rounded-lg">
                          <ToolTip
                            placement="top"
                            title={<HighlightSearchText text={col.name} />}
                            tooltipActive={tooltipShow}
                            displayTooltipOnOverflow
                          >
                            <span
                              onMouseEnter={() => {
                                setTooltipShow(true);
                              }}
                              className="col-txt whitespace-nowrap overflow-ellipsis overflow-hidden"
                              style={{
                                maxWidth: '100px',
                              }}
                            >
                              <HighlightSearchText text={col.name} />
                            </span>
                          </ToolTip>
                        </div>
                      </div>
                      <div className="flex items-center text-pv-text-secondary-text">
                        <ToolTip
                          placement="top"
                          title={col.tableName}
                          tooltipActive={tooltipShow}
                          displayTooltipOnOverflow
                        >
                          <span
                            onMouseEnter={() => {
                              setTooltipShow(true);
                            }}
                            className="col-txt whitespace-nowrap overflow-ellipsis overflow-hidden"
                            style={{
                              maxWidth: '150px',
                            }}
                          >
                            {col.tableName}
                          </span>
                        </ToolTip>
                      </div>
                    </div>
                  );
                })}
                {columnPageNumber <= columnPageCount &&
                  colList?.length < columnListData.data?.pages?.[0]?.total && (
                    <div className="flex flex-col">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div className="flex justify-between py-1">
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '40%' : '20%'}
                            height={20}
                            sx={{ bgcolor: '#EEF0F7' }}
                          />
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '20%' : '30%'}
                            height={20}
                            sx={{ bgcolor: '#EEF0F7' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span
                  className="text-xs px-2 py-1 text-pv-text-secondary-text"
                  style={{
                    height: '28px',
                  }}
                >
                  No results found. Try refining your search.
                </span>
                <Button
                  btnColor="secondary"
                  btnSize="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setImmediateSearchQuery('');
                  }}
                >
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
